/**
 * Edit Dietician Profile Page Component
 *
 * Allows dietitians to edit their existing profile information.
 * Pre-populates form with current profile data and handles updates.
 *
 * @module components/pages/EditDieticianProfilePage
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useDieticianProfile } from '@/hooks/useDieticianProfile';
import { apiService } from '@/services/ApiService';
import { BaseApiError } from '@/services/shared';
import { DieticianProfileForm } from '@/components/dietician-registration/DieticianProfileForm';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import type {
  DieticianProfileApiRequest,
  DieticianProfileApiResponse
} from '@/types/dieticianRegistration';
import type { HealthCondition } from '@/types/referral';
import { LanguageCode } from '@/types/languageCodes';
import { Layout } from '../layout';
import { FileUtils } from '@/utils/fileUtils';
import type { DieticianProfileFormData } from '@/hooks/useDieticianRegistrationFlow';
import { getLogger } from '@/services/shared/LoggerFactory';
import { BackButton } from '../ui/BackButton';
import { mapLanguageCodesToLabels } from '@/utils/languageUtils';
import { feeFieldsToPolicy, policyToFeeFields } from '@/utils/feePolicy';

/**
 * Transform API response to form data format
 */
const transformApiToFormData = (apiData: DieticianProfileApiResponse): Partial<DieticianProfileFormData> => {
  return {
    firstName: apiData.first_name || '',
    lastName: apiData.last_name || '',
    areasOfPractice: (apiData.areas_of_practice || []) as HealthCondition[],
    languagesSpoken: apiData.languages_spoken || [LanguageCode.English],
    phone: {
      countryCode: '+61', // Default to Australia
      isoCode: 'AU', // Default to Australia
      number: (() => {
        // Handle phone data from API - remove +61 prefix if present
        if (!apiData.phone) return '';

        const phoneNumber = apiData.phone;
        return phoneNumber.startsWith('+61') ? phoneNumber.slice(3) : phoneNumber;
      })(),
    },
    apdNumber: apiData.apd_id || '',
    practiceAddress: {
      streetAddress: apiData.practice_street_address || '',
      city: apiData.practice_city || '',
      state: apiData.practice_state || '',
      postalCode: apiData.practice_postal_code || '',
      country: apiData.practice_country || 'AU',
    },
    feePolicy: policyToFeeFields(apiData.payment_policy ?? null),
    // Note: photo handling would need additional implementation for existing photos
  };
};

/**
 * Transform form data to API request format
 */
const transformFormToApiData = async (formData: DieticianProfileFormData): Promise<DieticianProfileApiRequest> => {
  let photoData = undefined;
  if(formData.photo) {
    photoData = await FileUtils.safeConvertToBase64(formData.photo)
  }

  return {
    first_name: formData.firstName,
    last_name: formData.lastName,
    phone: {
      number: formData.phone.number,
      country_code: formData.phone.countryCode,
      iso_code: formData.phone.isoCode,
    },
    professional_info: {
      apd_id: formData.apdNumber,
      areas_of_practice: formData.areasOfPractice,
    },
    practice_address: {
      street_address: formData.practiceAddress.streetAddress,
      city: formData.practiceAddress.city,
      state: formData.practiceAddress.state,
      postal_code: formData.practiceAddress.postalCode,
      country: formData.practiceAddress.country,
    },
    languages_spoken: formData.languagesSpoken,
    availability: {
      calendar_integration: [],
    },
    consent: {
      terms_of_service: true,
      privacy_policy: true,
      professional_standards: true,
    },
    photo: photoData,
    payment_policy: feeFieldsToPolicy(formData.feePolicy),  // always sent (design D22)
  };
};

/**
 * Edit Dietician Profile Page Component
 */
export const EditDieticianProfilePage: React.FC = () => {
  const logger = getLogger();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { profile, isLoading: profileLoading, error: profileError, fetchProfile } = useDieticianProfile();
  const languageLabels = useMemo(
    () => mapLanguageCodesToLabels(profile?.languages_spoken || []),
    [profile?.languages_spoken]
  );

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
  const [initialFormData, setInitialFormData] = useState<Partial<DieticianProfileFormData> | undefined>();

  // Transform profile data to form data when profile loads
  useEffect(() => {
    if (profile) {
      setInitialFormData(transformApiToFormData(profile));
    }
  }, [profile]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (formData: DieticianProfileFormData) => {
    setIsSubmitting(true);
    setSubmitErrors({});

    try {
      // Transform form data to API format
      const apiData = await transformFormToApiData(formData);

      // Update profile using unified ApiService
      const response = await apiService.dietician.updateProfile(apiData);

      if (response.success) {
        // Refresh profile data to get updated information
        await fetchProfile();

        // Navigate back to dashboard
        navigate('/dashboard');
      } else {
        setSubmitErrors({ form: 'Failed to update profile. Please try again.' });
      }
    } catch (error) {
      logger.error('Profile update error:', error);

      if (error instanceof BaseApiError) {
        setSubmitErrors({ form: error.message, ...(error.fieldErrors ?? {}) });
      } else {
        setSubmitErrors({ form: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [fetchProfile, navigate]);

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Loading state
  if (profileLoading) {
    return (
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <Card className="p-6">
            <div className="animate-pulse space-y-6" data-testid="loading-skeleton" aria-label="Loading profile information">
              <div className="h-8 bg-neutral-300 rounded w-1/2" data-testid="skeleton-title"></div>
              <div className="h-4 bg-neutral-300 rounded w-3/4" data-testid="skeleton-subtitle"></div>
              <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-12 bg-neutral-300 rounded" data-testid={`skeleton-field-${i}`}></div>
                ))}
              </div>
              <div className="flex gap-4">
                <div className="h-10 bg-neutral-300 rounded w-24" data-testid="skeleton-button-1"></div>
                <div className="h-10 bg-neutral-300 rounded w-32" data-testid="skeleton-button-2"></div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (profileError) {
    return (
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-error-900/20 text-error-400' : 'bg-error-50 text-error-600'
              }`} data-testid="error-container">
                <TitleText level={3} className="mb-2">
                  Unable to Load Profile
                </TitleText>
                <BodyText>
                  {profileError.message || 'Failed to load your profile information.'}
                </BodyText>
              </div>
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => fetchProfile()}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleCancel}>
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // No profile data
  if (!profile || !initialFormData) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="p-6">
            <div className="text-center space-y-4">
              <TitleText level={3}>
                Profile Not Found
              </TitleText>
              <BodyText variant="muted">
                No profile information was found. Please complete your registration first.
              </BodyText>
              <Button variant="outline" onClick={handleCancel}>
                Back to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <div className="flex items-center gap-4 mb-4">
            <BackButton fallback='/dashboard'/>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <TitleText level={1} className="mb-2">
              Edit Profile
            </TitleText>
            <BodyText variant="secondary">
              Update your professional information and credentials
            </BodyText>
          </div>

          {/* Form Card */}
          <Card className="p-6" data-testid="form-card">
            {languageLabels.length > 0 && (
              <div className="mb-6">
                <BodyText variant="secondary" className="mb-2">
                  Languages spoken
                </BodyText>
                <div className="flex flex-wrap gap-2" aria-label="Languages spoken preview">
                  {languageLabels.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <DieticianProfileForm
              mode="edit"
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              initialData={initialFormData}
              isLoading={isSubmitting}
              errors={submitErrors}
            />
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default EditDieticianProfilePage;